/**
 * TV mode — layer 4 of docs/substrate-interaction-design.md.
 *
 * A page-level full-screen takeover for a wall-mounted display. TV mode
 * is a renderer-wrapper concern, never a per-primitive one: the host's
 * TV layout provides this context and PanelRenderer reads it to turn on
 * auto-cycle for every primitive that supports a `cycle` prop (with a
 * default interval when the spec didn't set one) and to honor the
 * display-wide pause (Space).
 */

import { createContext, useContext } from "react";

export const TV_DEFAULT_CYCLE_INTERVAL_MS = 4000;
/** Panel + renderer data refetch cadence in TV mode. Live writes still arrive over BrowserBus within seconds. */
export const TV_REFRESH_INTERVAL_MS = 5 * 60_000;

/** Primitive kinds whose renderer honors `cycle`. Keep in sync with PanelNodeInner's cycleLength switch. */
export const CYCLE_CAPABLE_KINDS: ReadonlySet<string> = new Set(["list", "news_feed"]);

export interface TvModeState {
  active: boolean;
  paused: boolean;
  defaultIntervalMs: number;
}

export const TV_MODE_OFF: TvModeState = { active: false, paused: false, defaultIntervalMs: TV_DEFAULT_CYCLE_INTERVAL_MS };

export const TvModeContext = createContext<TvModeState>(TV_MODE_OFF);

export function useTvMode(): TvModeState {
  return useContext(TvModeContext);
}

/**
 * The cycle config a primitive should run with. Outside TV mode the
 * spec's own `cycle` is used unchanged (default off). In TV mode every
 * cycle-capable primitive cycles; an interval the AI set wins.
 */
export function effectiveCycle(
  kind: string | undefined,
  specCycle: { interval_ms?: number } | null | undefined,
  tv: TvModeState,
): { interval_ms?: number } | undefined {
  const own = specCycle && typeof specCycle === "object" ? specCycle : undefined;
  if (!tv.active) return own;
  if (!kind || !CYCLE_CAPABLE_KINDS.has(kind)) return own;
  const interval = typeof own?.interval_ms === "number" && Number.isFinite(own.interval_ms) && own.interval_ms > 0
    ? own.interval_ms
    : tv.defaultIntervalMs;
  return { ...own, interval_ms: interval };
}

export type TvKeyAction = "exit" | "toggle_pause" | "toggle_fullscreen" | null;

/** Keyboard contract for the TV layout. Typing in a form field is never hijacked. */
export function tvKeyAction(key: string, target?: { tagName?: string; isContentEditable?: boolean } | null): TvKeyAction {
  const tag = target?.tagName?.toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select" || target?.isContentEditable) return null;
  if (key === "Escape" || key === "Esc") return "exit";
  if (key === " " || key === "Spacebar") return "toggle_pause";
  if (key === "f" || key === "F") return "toggle_fullscreen";
  return null;
}
